import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

interface TurnstileApi {
  render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const HOST_ID = 'cf-turnstile-host';
const OVERLAY_ID = 'cf-turnstile-overlay';
const PROTECTED_RESOURCES = ['reservations', 'online_orders', 'customers'];
const EXECUTE_TIMEOUT_MS = 60000;

@Injectable({
  providedIn: 'root'
})
export class TurnstileService {
  private scriptPromise: Promise<void> | null = null;
  private widgetId: string | null = null;
  private pending: { resolve: (token: string) => void; reject: (err: Error) => void } | null = null;
  private usedToken = false;

  protects(resourcePlural: string): boolean {
    return !!environment.turnstile_site_key && PROTECTED_RESOURCES.indexOf(resourcePlural) !== -1;
  }

  warmup(): void {
    if (!environment.turnstile_site_key) {
      return;
    }
    this.ensureWidget().catch(err => {
      console.log('Turnstile warmup failed', err);
    });
  }

  getToken(): Promise<string> {
    if (!environment.turnstile_site_key) {
      return Promise.resolve('');
    }

    return this.ensureWidget().then(() => new Promise<string>((resolve, reject) => {
      if (this.pending) {
        this.pending.reject(new Error('Turnstile already running'));
        this.pending = null;
      }

      const widgetId = this.widgetId;
      const api = window.turnstile;
      if (!widgetId || !api) {
        reject(new Error('Turnstile widget not ready'));
        return;
      }

      let timer = 0;
      const wrapResolve = (token: string) => {
        window.clearTimeout(timer);
        this.pending = null;
        this.usedToken = true;
        this.setOverlayVisible(false);
        resolve(token);
      };
      const wrapReject = (err: Error) => {
        window.clearTimeout(timer);
        this.pending = null;
        this.setOverlayVisible(false);
        reject(err);
      };

      this.pending = { resolve: wrapResolve, reject: wrapReject };
      timer = window.setTimeout(() => wrapReject(new Error('Turnstile timeout')), EXECUTE_TIMEOUT_MS);

      try {
        // reset() before the first token can fire error-callback. Only
        // reset when a previous token was already consumed.
        if (this.usedToken) {
          api.reset(widgetId);
        }
        api.execute(widgetId);
      } catch (err) {
        wrapReject(err instanceof Error ? err : new Error('Turnstile execute failed'));
      }
    }));
  }

  private ensureWidget(): Promise<void> {
    return this.loadScript().then(() => {
      if (this.widgetId && window.turnstile) {
        return;
      }

      const host = document.getElementById(HOST_ID);
      const api = window.turnstile;
      if (!host) {
        throw new Error('Turnstile host element missing');
      }
      if (!api) {
        throw new Error('Turnstile API missing');
      }

      this.widgetId = api.render(host, {
        sitekey: environment.turnstile_site_key,
        execution: 'execute',
        appearance: 'interaction-only',
        language: 'pt-BR',
        'response-field': false,
        'before-interactive-callback': () => this.setOverlayVisible(true),
        'after-interactive-callback': () => {},
        callback: (token: string) => {
          if (this.pending) {
            const pending = this.pending;
            this.pending = null;
            pending.resolve(token);
          }
        },
        'error-callback': (code: string | number) => {
          // Firefox often logs a 401 on Cloudflare's Private Access Token
          // probe. That is expected and must not abort the submit.
          console.log('Turnstile error-callback', code);
          if (String(code) === '401' || code === 401) {
            return;
          }
          if (this.pending) {
            const pending = this.pending;
            this.pending = null;
            pending.reject(new Error('Turnstile error ' + code));
          }
        },
        'timeout-callback': () => {
          if (this.pending) {
            const pending = this.pending;
            this.pending = null;
            pending.reject(new Error('Turnstile interactive timeout'));
          }
        },
        'unsupported-callback': () => {
          if (this.pending) {
            const pending = this.pending;
            this.pending = null;
            pending.reject(new Error('Turnstile unsupported browser'));
          }
        }
      });
    });
  }

  private loadScript(): Promise<void> {
    if (window.turnstile) {
      return Promise.resolve();
    }
    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`) as HTMLScriptElement;
      if (existing) {
        if (window.turnstile) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Turnstile script failed')));
        return;
      }

      const script = document.createElement('script');
      // Dynamically inserted scripts default to async; Cloudflare forbids
      // async/defer when the api.js is loaded this way (and we must not
      // call turnstile.ready() either). onload is enough.
      script.async = false;
      script.src = SCRIPT_SRC;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Turnstile script failed'));
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  private setOverlayVisible(visible: boolean): void {
    const overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) {
      return;
    }
    if (visible) {
      overlay.removeAttribute('hidden');
    } else {
      overlay.setAttribute('hidden', '');
    }
  }
}
